import { WEBSITE_DOMAIN } from '@application/constants';
import {
  addTrailingSlash,
  joinWithAnd,
  translatedFieldWithFallback,
  useRouter,
} from '@application/utils';
import type { BreederGetBreederQuery } from '@gql/graphql';
import { CountryCode } from '@gql/graphql';
import { useMemo } from 'react';
import { useLocation } from 'react-router';

type TBreeder = NonNullable<BreederGetBreederQuery['findManyDogBreeder']>[number];

export const useSeo = (breeder?: TBreeder, filteredBreeds?: TBreeder['breederBreeds']) => {
  const { translate, language, translatedRoute, countryCode } = useRouter();
  const { pathname } = useLocation();

  const baseBreedName = useMemo(() => {
    return filteredBreeds?.length
      ? filteredBreeds[0].dogBreed.baseBreedName ?? filteredBreeds[0].dogBreed.name
      : null;
  }, [filteredBreeds]);

  const breedNames = useMemo(() => {
    return (filteredBreeds ?? [])
      .map(({ dogBreed }) => {
        return (
          (dogBreed?.translations ?? []).find((x) => x.language === language)?.name ?? undefined
        );
      })
      .filter((x) => !!x);
  }, [filteredBreeds, language]);

  const title = useMemo(() => {
    if (!breedNames?.length || !breeder?.account.address?.country)
      return translate('breederDetails.seo.titleEmpty');
    const names = joinWithAnd(translate, breedNames ?? []);
    return translate('breederDetails.seo.title')
      .replaceAll('$breeds', names ?? '')
      .replaceAll(
        '$country',
        translate(`enum.country.${breeder?.account.address?.country ?? CountryCode.BE}`),
      );
  }, [breedNames, breeder?.account.address?.country, translate]);

  const titleMultipleNames = useMemo(() => {
    if (!breedNames?.length || !breeder?.account.address?.country)
      return translate('breederDetails.header.titleEmpty');
    const names = joinWithAnd(translate, breedNames ?? []);
    return translate('breederDetails.header.title')
      .replaceAll('$breeds', names ?? '')
      .replaceAll(
        '$country',
        translate(`enum.country.${breeder?.account.address?.country ?? CountryCode.BE}`),
      );
  }, [breedNames, breeder?.account.address?.country, translate]);

  const description = useMemo(() => {
    if (!breedNames?.length || !breeder?.account.address?.country)
      return translate('breederDetails.seo.descriptionEmpty');
    const names = joinWithAnd(translate, breedNames ?? []);
    return translate('breederDetails.seo.description')
      .replaceAll('$breeds', names ?? '')
      .replaceAll(
        '$country',
        translate(`enum.country.${breeder?.account.address?.country ?? CountryCode.BE}`),
      );
  }, [breedNames, breeder?.account.address?.country, translate]);

  const keywords = useMemo(() => {
    return translatedFieldWithFallback(language, breeder?.translations, 'seoKeywords');
  }, [breeder?.translations, language]);

  const breederName = useMemo(() => {
    return breeder?.breedingName ?? breeder?.displayName ?? undefined;
  }, [breeder?.breedingName, breeder?.displayName]);

  const canonicalUrl = useMemo(() => {
    const url = `${WEBSITE_DOMAIN}${translatedRoute('breederList')}${
      baseBreedName ? encodeURI(baseBreedName) : ''
    }`;

    return addTrailingSlash(url);
  }, [baseBreedName, translatedRoute]);

  return {
    title,
    description,
    keywords,
    breederName,
    breedNames,
    canonicalUrl,
    titleMultipleNames,
  };
};
